import React, { useEffect, useRef, useState } from 'react';
import profileImg from './images/profile-1.png';

// ── Hooks ────────────────────────────────────────────────────────

function useInView({ threshold = 0.2, rootMargin = '0px 0px -10% 0px' } = {}) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current || inView) return;
    if (typeof IntersectionObserver === 'undefined') { setInView(true); return; }
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold, rootMargin }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [inView, threshold, rootMargin]);
  return [ref, inView] as const;
}

function useScrollProgress(targetRef: React.RefObject<HTMLDivElement | null>) {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    let rafId: number | null = null;
    const update = () => {
      rafId = null;
      const el = targetRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const total = rect.height + vh;
      const scrolled = vh - rect.top;
      setProgress(Math.max(0, Math.min(1, scrolled / total)));
    };
    const onScroll = () => { if (rafId == null) rafId = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [targetRef]);
  return progress;
}

function useParallaxOffset(targetRef: React.RefObject<HTMLDivElement | null>, intensity: number) {
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    if (!intensity) { setOffset(0); return; }
    let rafId: number | null = null;
    const update = () => {
      rafId = null;
      const el = targetRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      setOffset((rect.top + rect.height / 2 - vh / 2) * intensity);
    };
    const onScroll = () => { if (rafId == null) rafId = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [targetRef, intensity]);
  return offset;
}

// ── Data ─────────────────────────────────────────────────────────

const EXPERIENCE_GROUPS = [
  {
    company: 'SENPRO',
    span: '2025 — Present',
    roles: [
      {
        title: 'Junior Software Developer',
        period: '2025 — Present',
        description:
          'Prototyped an AI-powered nutrition feature enabling users to upload food images and receive estimated nutritional breakdowns. Explored agentic AI workflows to assess meal suitability against user goals and provide structured, goal-oriented feedback.',
      },
    ],
  },
  {
    company: 'Innovatedge',
    span: '2022 — Present',
    roles: [
      {
        title: 'Software Developer',
        period: '2025 — Present',
        description:
          'Built an automated contact enrichment pipeline using n8n to process large HubSpot contact lists, including email discovery, data standardisation, and job change detection. Developed an AI-driven scoring system to prioritise contacts for marketing campaigns based on predefined targeting criteria.',
      },
      {
        title: 'UI/UX Design Intern',
        period: '2022 — 2024',
        description:
          "Worked with and learned from a professional UX designer to re-design Innovatedge's website. Also worked on the frontend of the website, implementing the design into WordPress.",
      },
    ],
  },
  {
    company: 'The Female Drive',
    span: '2023',
    roles: [
      {
        title: 'Macquarie University Computing Industry Project',
        period: '2023',
        description:
          'As part of a team of 4, worked on a project to design and create a full stack application for a client. Responsible for the frontend and some of the backend of the React application.',
      },
    ],
  },
];

// ── Experience sub-components ─────────────────────────────────────

const EASE = 'cubic-bezier(.16,1,.3,1)';
const PARALLAX_INTENSITY = 0;

const PINK = '#f3a6c4';
const PINK_SOFT = '#f5b6cd';
const INK = '#2c2c3e';
const INK_SOFT = '#5a5a6e';
const DIVIDER = 'rgba(44,44,62,0.08)';
const BG = '#f5eef2';

function PeriodLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 11.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: INK_SOFT, fontWeight: 500 }}>
      {children}
    </div>
  );
}

function RoleRow({ role, isFirst, delay }: { role: typeof EXPERIENCE_GROUPS[0]['roles'][0]; isFirst: boolean; delay: number }) {
  const [ref, inView] = useInView({ threshold: 0.15 });
  return (
    <div
      ref={ref}
      style={{
        position: 'relative',
        paddingTop: isFirst ? 0 : 22,
        marginTop: isFirst ? 0 : 22,
        borderTop: isFirst ? 'none' : '1px dashed rgba(243,166,196,0.45)',
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(12px)',
        transition: `opacity 800ms ${EASE} ${delay}ms, transform 800ms ${EASE} ${delay}ms`,
      }}
    >
      <PeriodLabel>{role.period}</PeriodLabel>
      <div
        style={{
          marginTop: 8,
          fontFamily: '"Playfair Display", Georgia, serif',
          fontWeight: 700,
          fontSize: role.title.length > 38 ? 22 : 26,
          lineHeight: 1.25,
          color: INK,
          letterSpacing: '-0.005em',
        }}
      >
        {role.title}
      </div>
      <p style={{ margin: '12px 0 0', fontSize: 15, lineHeight: 1.7, color: INK, opacity: 0.75, maxWidth: 680 }}>
        {role.description}
      </p>
    </div>
  );
}

function CompanyGroup({
  group,
  isFirst,
  isLast,
}: {
  group: typeof EXPERIENCE_GROUPS[0];
  isFirst: boolean;
  isLast: boolean;
}) {
  const [ref, inView] = useInView({ threshold: 0.18 });
  const dotRef = useRef<HTMLDivElement>(null);
  const parallaxY = useParallaxOffset(dotRef, PARALLAX_INTENSITY);

  return (
    <div
      ref={ref}
      style={{
        position: 'relative',
        paddingTop: isFirst ? 0 : 48,
        paddingBottom: 48,
        borderBottom: isLast ? 'none' : `1px solid ${DIVIDER}`,
      }}
    >
      {/* Anchor dot */}
      <div
        ref={dotRef}
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: -36,
          top: isFirst ? 8 : 58,
          width: 12,
          height: 12,
          borderRadius: '50%',
          background: PINK,
          boxShadow: `0 0 0 4px ${BG}`,
          opacity: inView ? 1 : 0,
          transform: inView ? `translateY(${parallaxY}px) scale(1)` : `translateY(${parallaxY}px) scale(0.4)`,
          transition: `opacity 600ms ${EASE} 120ms, transform 600ms ${EASE} 120ms`,
          willChange: 'transform',
        }}
      />

      {/* Company header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 18,
          marginBottom: 28,
          flexWrap: 'wrap' as const,
          opacity: inView ? 1 : 0,
          transform: inView ? 'translateY(0)' : 'translateY(14px)',
          transition: `opacity 800ms ${EASE}, transform 800ms ${EASE}`,
        }}
      >
        <div
          style={{
            fontFamily: '"Playfair Display", Georgia, serif',
            fontStyle: 'italic',
            fontWeight: 400,
            fontSize: 32,
            color: PINK,
            letterSpacing: '-0.005em',
            lineHeight: 1,
          }}
        >
          {group.company}
        </div>
        <PeriodLabel>{group.span}</PeriodLabel>
      </div>

      {group.roles.map((r, ri) => (
        <RoleRow key={ri} role={r} isFirst={ri === 0} delay={140 + ri * 110} />
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────

const About: React.FC = () => {
  const fullStoryTitle = 'My Story';
  const [storyTitle, setStoryTitle] = useState('');

  useEffect(() => {
    let currentIndex = 0;
    const typingInterval = setInterval(() => {
      currentIndex += 1;
      setStoryTitle(fullStoryTitle.slice(0, currentIndex));
      if (currentIndex === fullStoryTitle.length) clearInterval(typingInterval);
    }, 120);
    return () => clearInterval(typingInterval);
  }, []);

  const skills = [
    { category: 'Frontend', items: ['React.js', 'JavaScript & TypeScript', 'Tailwind CSS', 'React Native', 'HTML5/CSS3'] },
    { category: 'Backend', items: ['Node.js', 'Express.js', 'Python', 'Java', 'MongoDB'] },
    { category: 'Tools', items: ['Git', 'VS Code', 'Figma', 'Cursor', 'Claude Code'] },
    { category: 'Other', items: ['REST APIs', 'MERN Stack', 'Prompt Engineering', 'Agentic AI', 'Responsive Design'] },
  ];

  // Spine draw
  const spineRef = useRef<HTMLDivElement>(null);
  const progress = useScrollProgress(spineRef);
  const drawn = Math.max(0, Math.min(1, (progress - 0.15) / 0.55));

  // Section heading animation
  const [headRef, headIn] = useInView({ threshold: 0.4 });

  return (
    <div className="pt-16">
      {/* My Story Section */}
      <section className="section-padding">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-4xl md:text-5xl font-elegant font-bold mb-8 gradient-text leading-tight pb-2">
                {storyTitle}
                {storyTitle.length < fullStoryTitle.length && (
                  <span className="text-soft-pink animate-pulse">|</span>
                )}
              </h2>
              <div className="space-y-4 text-gray-900 leading-relaxed">
                <p>
                  I'm a software developer based in Sydney, Australia, focused on building scalable web applications and solving real-world problems through pragmatic design and engineering.
                </p>
                <p>
                  I recently completed my Bachelor of Information Technology at Macquarie University, majoring in Web and Mobile App Development.
                </p>
                <p>
                  I work across the full stack with the MERN ecosystem, with an emphasis on building robust, performant systems and clean, maintainable architectures. My experience spans developing production-ready applications, integrating APIs, and designing end-to-end user workflows.
                </p>
                <p>
                  I'm also deeply interested in the evolving role of AI in tech. I've begun integrating AI features into my projects and actively explore how emerging tools can enhance user experiences, boost productivity, and transform how we build software.
                </p>
                <p>
                  I have full working rights in both Australia and the United States, and I'm open to flexible, remote-friendly opportunities across both regions.
                </p>
              </div>
            </div>
            <div className="flex justify-center lg:justify-end">
              <img src={profileImg} alt="Profile" className="w-80 h-auto object-contain" />
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="section-padding bg-white/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-elegant font-semibold text-center mb-12 gradient-text">
            Skills & Technologies
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {skills.map((skillGroup, index) => (
              <div key={index} className="card">
                <h3 className="text-xl font-elegant font-semibold mb-4 gradient-text">
                  {skillGroup.category}
                </h3>
                <div className="space-y-2">
                  {skillGroup.items.map((skill, skillIndex) => (
                    <div key={skillIndex} className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-soft-pink rounded-full"></div>
                      <span className="text-gray-600 font-clean">{skill}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Section — minimal index timeline */}
      <section className="section-padding">
        <div className="max-w-4xl mx-auto px-4">
          {/* Heading */}
          <div
            ref={headRef}
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 16,
              marginBottom: 64,
              opacity: headIn ? 1 : 0,
              transform: headIn ? 'translateY(0)' : 'translateY(12px)',
              transition: `opacity 900ms ${EASE}, transform 900ms ${EASE}`,
            }}
          >
            <h2
              style={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontWeight: 700,
                fontSize: 'clamp(48px, 8vw, 72px)',
                lineHeight: 1,
                color: PINK_SOFT,
                margin: 0,
                letterSpacing: '-0.015em',
              }}
            >
              Experience
            </h2>
          </div>

          {/* Timeline */}
          <div ref={spineRef} style={{ position: 'relative', paddingLeft: 36 }}>
            {/* Dashed spine (always visible) */}
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: 4,
                top: 10,
                bottom: 10,
                width: 1,
                background: `repeating-linear-gradient(to bottom, ${PINK} 0 4px, transparent 4px 9px)`,
              }}
            />
            {/* Solid overlay that grows as you scroll */}
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: 4,
                top: 10,
                bottom: 10,
                width: 1.5,
                marginLeft: -0.25,
                background: PINK,
                transformOrigin: 'top',
                transform: `scaleY(${drawn})`,
                transition: 'transform 120ms linear',
                willChange: 'transform',
              }}
            />

            {EXPERIENCE_GROUPS.map((g, gi) => (
              <CompanyGroup
                key={gi}
                group={g}
                isFirst={gi === 0}
                isLast={gi === EXPERIENCE_GROUPS.length - 1}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Interests Section */}
      <section className="section-padding bg-white/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-elegant font-semibold text-center mb-12 gradient-text">
            Interests & Learning
          </h2>
          <div className="max-w-4xl mx-auto">
            <div className="card">
              <div className="space-y-6 text-gray-600 leading-relaxed">
                <p className="text-lg">
                  I'm passionate about staying at the forefront of technology, particularly in the rapidly evolving field of artificial intelligence. I'm actively learning prompt engineering and exploring how AI can enhance the development process.
                </p>
                <p className="text-lg">
                  I believe that understanding and leveraging AI tools is crucial for modern developers. I'm constantly experimenting with new AI-powered development tools, learning effective prompt engineering techniques, and finding ways to integrate AI into my workflow to boost productivity and creativity.
                </p>
                <p className="text-lg">
                  Beyond AI, I'm always excited to learn new technologies and frameworks. I enjoy exploring emerging web technologies, experimenting with new design patterns, and staying current with industry best practices to deliver the best possible user experiences and software solutions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About; 